import { useTranslation } from 'react-i18next';
import { CalendarClock } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/shared/components/ui/tooltip';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/shared/components/ui/select';
import { Switch } from '@/shared/components/ui/switch';
import { Label } from '@/shared/components/ui/label';
import type { GanttZoom, GanttGroupBy } from '../../types';

const ZOOMS: readonly GanttZoom[] = ['day', 'week', 'month'];
const GROUP_BYS: readonly GanttGroupBy[] = ['none', 'status', 'priority', 'assignee', 'group'];

interface GanttToolbarProps {
  zoom: GanttZoom;
  onZoomChange: (zoom: GanttZoom) => void;
  groupBy: GanttGroupBy;
  onGroupByChange: (groupBy: GanttGroupBy) => void;
  showCompleted: boolean;
  onShowCompletedChange: (value: boolean) => void;
  onTodayClick: () => void;
}

export function GanttToolbar({
  zoom,
  onZoomChange,
  groupBy,
  onGroupByChange,
  showCompleted,
  onShowCompletedChange,
  onTodayClick,
}: GanttToolbarProps) {
  const { t } = useTranslation('tasks');

  return (
    <div
      data-testid="tasks-gantt-toolbar"
      className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2"
    >
      <div className="flex items-center gap-3">
        <Select value={groupBy} onValueChange={(value) => onGroupByChange(value as GanttGroupBy)}>
          <SelectTrigger
            data-testid="tasks-gantt-groupby-select"
            className="h-8 w-[170px]"
            aria-label={t('gantt.groupBy.label')}
          >
            <SelectValue placeholder={t('gantt.groupBy.label')} />
          </SelectTrigger>
          <SelectContent>
            {GROUP_BYS.map((option) => (
              <SelectItem key={option} value={option}>
                {t(`gantt.groupBy.${option}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2">
          <Switch
            id="gantt-show-completed"
            data-testid="tasks-gantt-show-completed-toggle"
            checked={showCompleted}
            onCheckedChange={onShowCompletedChange}
          />
          <Label htmlFor="gantt-show-completed" className="text-sm text-muted-foreground">
            {t('gantt.showCompleted')}
          </Label>
        </div>
      </div>

      <div className="flex items-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onTodayClick}
              data-testid="tasks-gantt-today-button"
              aria-label={t('gantt.today')}
            >
              <CalendarClock />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('gantt.today')}</TooltipContent>
        </Tooltip>

        <div className="ml-1 flex items-center gap-0.5 rounded-md border border-border p-0.5">
          {ZOOMS.map((option) => (
            <Button
              key={option}
              variant={zoom === option ? 'default' : 'ghost'}
              size="xs"
              onClick={() => onZoomChange(option)}
              data-testid={`tasks-gantt-zoom-${option}`}
            >
              {t(`gantt.zoom.${option}`)}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}
